import { call, put, takeEvery } from "redux-saga/effects";

import * as apis from "../../../network/apis/companyPortalEvaluations";
import {
  getEvaluationListRounds as getAdminEvaluations,
  getEvaluationListRoundsSupervisor,
  getEvaluationCompletedPendingList as getAdminEvaluationForms,
  getEvaluationCompletedPendingSuperVisorList,
  getViewFormDetailsSupervisor,
  submitSupervisorEvaluation,
} from "../../../network/apis/evaluation";
import { getEventRequestListMeta } from "../../../components/Events/Event/Applicants/eventRequestMappers";
import {
  normalizeEvaluationRounds,
  normalizeEvaluationForms,
  normalizeEvaluationFormDetails,
} from "../../../components/Events/Event/Evaluations/evaluationMappers";
import { APPLICANT_EVALUATION_STATUSES } from "../../../components/Events/Event/Evaluations/constants";
import { showErrorMsg, showSuccessMsg } from "../../../utils/Helpers";
import { isSupervisorManger } from "../../../utils/Permissions";
import * as actions from "./actions";
import * as types from "./types";

const extractListItems = (response) => response?.data?.data ?? [];
const isAdmin = (portal) => portal === "admin-portal";

const resolveEvaluationsListApi = (portal) => {
  if (isSupervisorManger()) return getEvaluationListRoundsSupervisor;
  if (isAdmin(portal)) return getAdminEvaluations;
  return apis.getCompanyEvaluations;
};

const resolveEvaluationFormsListApi = (portal) => {
  if (isSupervisorManger()) return getEvaluationCompletedPendingSuperVisorList;
  if (isAdmin(portal)) return getAdminEvaluationForms;
  return apis.getCompanyEvaluationForms;
};

const resolveEvaluationFormApi = () =>
  isSupervisorManger() ? getViewFormDetailsSupervisor : apis.getCompanyEvaluationForm;

const resolveSubmitEvaluationFormApi = () =>
  isSupervisorManger() ? submitSupervisorEvaluation : apis.submitCompanyEvaluationForm;

function* getCompanyEvaluationsSaga({ payload }) {
  try {
    const { portal, ...params } = payload || {};
    const response = yield call(resolveEvaluationsListApi(portal), params);
    yield put(
      actions.getCompanyEvaluationsResponse({
        list: normalizeEvaluationRounds(extractListItems(response)),
        meta: getEventRequestListMeta(response),
      }),
    );
  } catch (error) {
    yield call(showErrorMsg, error.response?.data?.message);
  }
}

function* createCompanyEvaluationSaga({ payload }) {
  try {
    const response = yield call(apis.createCompanyEvaluation, payload.eventId);
    yield call(showSuccessMsg, response.data?.message, true);
    if (payload.listParams) {
      yield put(actions.getCompanyEvaluationsRequest(payload.listParams));
    }
    payload.onSuccess?.(response);
  } catch (error) {
    yield call(showErrorMsg, error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* updateCompanyEvaluationSaga({ payload }) {
  try {
    const response = yield call(apis.updateCompanyEvaluation, {
      id: payload.id,
      evaluation: payload.evaluation,
    });
    yield call(showSuccessMsg, response.data?.message, true);
    if (payload.listParams) {
      yield put(actions.getCompanyEvaluationsRequest(payload.listParams));
    }
    payload.onSuccess?.(response);
  } catch (error) {
    yield call(showErrorMsg, error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* getCompanyEvaluationFormsSaga({ payload }) {
  try {
    const { portal, ...params } = payload || {};
    const response = yield call(resolveEvaluationFormsListApi(portal), params);
    yield put(
      actions.getCompanyEvaluationFormsResponse({
        list: normalizeEvaluationForms(extractListItems(response)),
        meta: getEventRequestListMeta(response),
      }),
    );
  } catch (error) {
    yield call(showErrorMsg, error.response?.data?.message);
  }
}

function* getCompanyEvaluationFormSaga({ payload }) {
  try {
    const response = yield call(resolveEvaluationFormApi(), payload.id);
    yield put(
      actions.getCompanyEvaluationFormResponse(
        normalizeEvaluationFormDetails(response?.data?.data ?? {}),
      ),
    );
    payload.onSuccess?.(response);
  } catch (error) {
    yield call(showErrorMsg, error.response?.data?.message);
    payload.onError?.(error);
  }
}

function* submitCompanyEvaluationFormSaga({ payload }) {
  try {
    const response = yield call(resolveSubmitEvaluationFormApi(), {
      id: payload.id,
      data: payload.data,
    });
    yield call(showSuccessMsg, response.data?.message, true);
    // Update the forms list in Redux so the row flips to "Done" without
    // requiring a full refetch. Use the API response body when available;
    // fall back to a minimal status-only update.
    const responseData = response?.data?.data;
    const normalized = responseData
      ? normalizeEvaluationFormDetails(responseData)
      : { id: String(payload.id), status: APPLICANT_EVALUATION_STATUSES.done };
    yield put(actions.submitCompanyEvaluationFormResponse(normalized));
    payload.onSuccess?.(response);
  } catch (error) {
    yield call(showErrorMsg, error.response?.data?.message);
    payload.onError?.(error);
  }
}

export default function* companyEvaluationsSagas() {
  yield takeEvery(types.GET_COMPANY_EVALUATIONS_REQUEST, getCompanyEvaluationsSaga);
  yield takeEvery(
    types.CREATE_COMPANY_EVALUATION_REQUEST,
    createCompanyEvaluationSaga,
  );
  yield takeEvery(
    types.UPDATE_COMPANY_EVALUATION_REQUEST,
    updateCompanyEvaluationSaga,
  );
  yield takeEvery(
    types.GET_COMPANY_EVALUATION_FORMS_REQUEST,
    getCompanyEvaluationFormsSaga,
  );
  yield takeEvery(
    types.GET_COMPANY_EVALUATION_FORM_REQUEST,
    getCompanyEvaluationFormSaga,
  );
  yield takeEvery(
    types.SUBMIT_COMPANY_EVALUATION_FORM_REQUEST,
    submitCompanyEvaluationFormSaga,
  );
}
